import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deactivateRentalPortalListing } from '../../../../../../../api'
import { QA_NAME_CONFIRMATION } from '../../../../../../../constants/qa'
import { useNotification } from '../../../../../../../hooks'
import { ConfirmationModal } from '../../../../../../Confirmation'
import {
  getIsListingChangedConflictError,
  getListingDeactivateConflictNotification,
  getListingUnitLabel,
} from '../../../../../../RentalPortalListing'
import { getListingStatusActionErrorText } from '../../../../../RentalPortalListings'
import { useListingProfileContext } from '../../../RentalPortalListingProfile.hooks'
import { getListingProfileLivePortalNames } from '../../../RentalPortalListingProfile.utils'
import {
  LISTING_STATUS_CHANGE_BUTTON_KEY,
  LISTING_STATUS_CHANGE_NOTIFICATION_KEY,
} from '../../RentalPortalListingProfileHeaderStatusSelect.constants'
import { getListingStatusChangeDescription } from '../../RentalPortalListingProfileHeaderStatusSelect.utils'

export function useListingProfileStatusChange() {
  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const { handleCloseModal, handleEntityReload, listingData, setModal } =
    useListingProfileContext()

  const [isStatusUpdating, setStatusUpdating] = useState(false)

  async function handleSubmitStatusChange(status) {
    setStatusUpdating(true)

    return deactivateRentalPortalListing({
      id: listingData.id,
      version: listingData.version,
    })
      .then(() => {
        handleCloseModal()

        handleAddSuccessNotification(
          LISTING_STATUS_CHANGE_NOTIFICATION_KEY[status]
        )

        return handleEntityReload()
      })
      .catch(error => {
        handleCloseModal()

        if (getIsListingChangedConflictError(error)) {
          handleAddInfoNotification(
            getListingDeactivateConflictNotification({ error, t })
          )

          return handleEntityReload()
        }

        handleAddErrorNotification({
          textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
          title: getListingStatusActionErrorText({ error, t }),
          onClickTextButton: () => handleOpenStatusConfirmation(status),
        })
      })
      .finally(() => {
        setStatusUpdating(false)
      })
  }

  function handleOpenStatusConfirmation(status) {
    async function handleSubmit() {
      return handleSubmitStatusChange(status)
    }

    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t(LISTING_STATUS_CHANGE_BUTTON_KEY[status]),
        }}
        description={getListingStatusChangeDescription({
          livePortalNames: getListingProfileLivePortalNames(listingData),
          name: getListingUnitLabel({ listing: listingData, t }),
          status,
          t,
        })}
        qaName={QA_NAME_CONFIRMATION}
        onCancel={handleCloseModal}
        onSubmit={handleSubmit}
      />
    )
  }

  function handleSelectStatus(option) {
    return option.disabled || option.value === listingData?.status
      ? null
      : handleOpenStatusConfirmation(option.value)
  }

  return { handleSelectStatus, isStatusUpdating }
}
